/**
 * TOSS Web Application — the top bar, one frame for both audiences
 *
 * The internal application and the customer portal draw the same bar: on the left, who or
 * where you are; on the right, the clocks and the actions. Until 2026-08-26 they drew it
 * twice — `Header.tsx` and `ClientHeader.tsx` — and the two copies had drifted on every
 * measurement that is not visible in a screenshot of one of them alone: 44 px icon buttons
 * against 36, a 24 px glyph against 20, a bell badge that was a dot on one surface and a
 * number on the other, and the same z-index written two different ways.
 *
 * This layer holds the frame and nothing else. It follows the rule the shared application
 * card established (`vault/knowledge/patterns/two audiences share a rendered node, never a
 * DTO.md`): it receives **nodes**, never data, and it has no idea which surface it is drawn
 * for — no `surface` prop, no capability object, no store, no query. ESLint pins that; the
 * folder may not import `features/**`, any API module, the auth store or a permission key.
 *
 * What each surface puts in the slots is its own business:
 *
 *  - internal — the drawer toggle on a phone; clocks, the term button, the calculator, the
 *    messenger and the bell on the right;
 *  - portal — the legal entity (which is a heading and, for a multi-company account, the
 *    control that switches it); clocks and the bell.
 *
 * **No title slot, and that is the point.** Both surfaces print the name of the screen on the
 * screen itself — the internal one through `PageHeader`, the portal through the lit row of its
 * sidebar. A bar that can print it again is how «Заявки» ended up over «Заявки» on seven
 * screens out of nine (decision Р33), and the internal header carried a `pageTitle` prop that
 * no caller had passed in a year: `uiStore.pageTitle` was empty on every screen of the
 * application. The slot is called `leading` because what goes there is not a title.
 */

import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface TopBarProps extends HTMLAttributes<HTMLElement> {
  /** The left slot: a menu toggle, a company switcher — never the name of the screen. */
  leading?: ReactNode;
  /** The right slot: clocks, groups, single actions. */
  actions?: ReactNode;
  /**
   * Height on a phone. 64 keeps the portal's shell honest — `ClientLayout` reserves exactly
   * that many pixels with a spacer, because the bar is `fixed` there (see `fixed` below).
   * The internal app is 56 on a phone and always has been.
   *
   * Above 767 both are 64: that is what lines the bar up with the sidebar's logo block.
   */
  phoneHeight?: 56 | 64;
  /**
   * Take the bar out of the flow and pin it to the top of the window.
   *
   * The portal's phone shell needs this and cannot use `sticky`: the global stylesheet puts
   * `overflow-x: hidden` on `<body>`, which makes the body a scroll container, and a `sticky`
   * element inside a container that does not itself scroll never sticks to anything. A caller
   * that sets this owes the layout a spacer of the same height.
   */
  fixed?: boolean;
  /**
   * Полоса 2 px, ложащаяся на нижнюю границу бара, — состояние соединения
   * (`ConnectionStatusThread`). Отдельный слот, а не `actions`: она `absolute` и обязана
   * мериться шириной БАРА, а не правой группой. Именно поэтому бар всегда `relative`.
   */
  underline?: ReactNode;
  /**
   * Чем бар является на экране.
   *
   * `bar` — полоса во всю ширину своей колонки, отделённая линией. Так он выглядит на телефоне,
   * где боковой карточки нет и делить экран не с чем.
   *
   * `card` — белая карточка на канве, как боковое меню и как содержимое (владелец, 27.08.2026:
   * «шапка тоже карточка, линии нет вовсе, отделяет цвет»). Линия ей не нужна: от содержимого её
   * отделяет серое поле.
   *
   * Тулбар экрана в карточку НЕ переезжает, хотя такой вариант собирали и смотрели: владелец
   * выбрал оставить его на своём месте, под шапкой. Поэтому карточка — это ровно одна строка, а
   * заголовок экрана печатает страница, как и раньше.
   *
   * Отступы вокруг карточки задаёт не бар, а оболочка: только она знает, по какой вертикали
   * выравнивать её с содержимым.
   */
  variant?: 'bar' | 'card';
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

// Written out rather than composed from `phoneHeight`: Tailwind scans source text, and a class
// built at runtime is a class that never reaches the stylesheet.
const HEIGHT_56 =
  'h-[calc(56px+env(safe-area-inset-top,0px))] lg:h-[calc(64px+env(safe-area-inset-top,0px))]';
const HEIGHT_64 = 'h-[calc(64px+env(safe-area-inset-top,0px))]';
/**
 * Карточка ниже полосы, и намеренно: «очень широкая по высоте, сделать поуже, чтобы места
 * меньше занимала» (владелец, 27.08.2026).
 *
 * 64 px были ростом ПОЛОСЫ во всю ширину окна, где высота — единственное, чем бар отделён от
 * страницы. Карточку отделяет поле вокруг, и лишние 12 px внутри неё — просто съеденный экран.
 * 52 = 40 (`--h-control`, рост переключателя компании и кнопок внутри) + по 6 сверху и снизу.
 */
const HEIGHT_CARD = 'h-[52px]';

// ============================================================================
// Component
// ============================================================================

export const TopBar = forwardRef<HTMLElement, TopBarProps>(
  (
    {
      leading,
      actions,
      underline,
      phoneHeight = 56,
      fixed = false,
      variant = 'bar',
      className = '',
      ...props
    },
    ref
  ) => {
    const isCard = variant === 'card';

    const classes = [
      isCard ? HEIGHT_CARD : phoneHeight === 64 ? HEIGHT_64 : HEIGHT_56,
      'flex items-center justify-between gap-3 lg:gap-4',
      // Резерв под вырез экрана. Работает там, где вебвью действительно уходит под системную
      // полосу: Android в standalone и Safari в ландшафте. На iOS в standalone `env()` здесь
      // равен нулю — `apple-mobile-web-app-status-bar-style: default` держит вебвью НИЖЕ
      // статус-бара, и полосу над ним красит система по `theme-color` (см.
      // `src/theme/themeColor.ts`). Прежняя редакция комментария обещала обратное — это верно
      // только для `black-translucent`, на который кабинет не переходит: он делает иконки
      // статус-бара белыми, а канва светлая.
      // Карточке это не нужно: она лежит внутри страницы, а не под вырезом экрана.
      isCard ? '' : 'pt-[env(safe-area-inset-top,0px)]',
      'px-4 lg:px-6',
      // Карточка — поверхность (владелец, 27.08.2026). Прежняя редакция красила бар цветом
      // КАРТОЧКИ во всю ширину колонки — и этот цвет носили три разные вещи сразу: навигация,
      // шапка и содержимое. Теперь белое означает «здесь содержание», а шапка на компьютере
      // сама стала таким объектом, отделённым полем, а не линией.
      // `overflow-hidden` — ради нити соединения: она `absolute` по нижней кромке и без обрезки
      // прошла бы по хорде за скруглённый угол.
      isCard
        ? 'app-surface overflow-hidden'
        // Полоса (телефон) красится канвой и отделяется линией — см. её ниже.
        : 'bg-[var(--color-bg-app)]',
      'flex-shrink-0 z-sticky',
      // Якорь для `underline`: без позиционирования нить уедет к ближайшему позиционированному
      // предку. Ветки взаимоисключающие не для красоты — Tailwind печатает `.relative` ПОСЛЕ
      // `.fixed`, и написанные вместе они дали бы `relative`, отклеив шапку телефона от верха.
      fixed ? 'fixed top-0 left-0 right-0' : 'relative',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <header ref={ref} className={classes} {...props}>
        {/* `flex-1` and not just `min-w-0`: without it this column sizes to its widest child's
            max-content, and a long company name settled at 144 px inside a 668 px bar —
            truncated to «АО «РЖД Логисти…» with half the header empty beside it. */}
        <div className="flex items-center gap-2 min-w-0 flex-1">{leading}</div>

        <div className="flex items-center gap-3 flex-shrink-0">{actions}</div>

        {/* Линия под полосой — элемент, а не `border-b`, и это не стилистика. Полоса живёт в
            колонке ПРАВЕЕ бокового меню, а меню с 27.08.2026 — карточка с полем вокруг: граница
            шла от самого края колонки и втыкалась ей в бок («эта линия верхнего меню врезается
            в боковое», владелец). Отступ слева ровно в зазор карточки уводит начало линии в
            серое поле. Ниже 768 бокового меню на экране нет — там линия идёт от края.

            Карточке линия не нужна вовсе: её отделяет поле.

            Порядок в разметке важен: нить соединения (`underline`) объявлена ПОСЛЕ и потому
            ложится поверх, как и было задумано, когда она перекрывала `border-b`. */}
        {!isCard && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute bottom-0 right-0 left-0 md:left-[var(--sidebar-gap)] h-px bg-[var(--color-border)]"
          />
        )}

        {underline}
      </header>
    );
  }
);

TopBar.displayName = 'TopBar';

export default TopBar;
